<template>
  <div class="wishlist">
    <div class="wishlist__block" v-if="productsInWishlist.length">
      <p class="wishlist__title">{{ t('profile.wishlist.title') }}</p>
      <div class="wishlist__list">
        <div
            class="wishlist__item"
            v-for="product in productsInWishlist"
            :key="product.node.uuid"
        >
          <div class="wishlist__item-button" @click="removeFromWishlist(product.node.uuid)">
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 10L1 1M10 1L1 10" stroke="#2F2F2F" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <p>{{ t('buttons.remove') }}</p>
          </div>
          <product-card
              :product="product.node"
          />
        </div>
      </div>
    </div>
    <div class="wishlist__empty" v-else>
      <svg width="224" height="182" viewBox="0 0 224 182" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M183.272 26.3643C188.26 31.2334 192 36.7555 194.494 42.9309C196.988 49.225 198.235 55.6081 198.235 62.0804C198.235 68.5526 196.988 74.8764 194.494 81.0517C192 87.3458 188.26 92.9867 183.272 97.9745L112.731 168.872C111.899 169.585 111.009 170.119 110.058 170.475C109.108 170.832 108.099 171.01 107.03 171.01C106.911 171.01 106.704 171.01 106.407 171.01C106.11 171.01 105.843 171.01 105.605 171.01C104.893 170.891 104.18 170.653 103.467 170.297C102.755 169.941 102.042 169.466 101.33 168.872L30.7885 97.9745C25.8007 92.9867 22.0599 87.3458 19.566 81.0517C17.0721 74.8764 15.8252 68.5526 15.8252 62.0804C15.8252 55.6081 17.0721 49.225 19.566 42.9309C22.0599 36.7555 25.8007 31.2334 30.7885 26.3643C35.42 21.6141 40.8234 17.9326 46.9988 15.32C53.1741 12.7073 59.6463 11.401 66.4155 11.401C73.1846 11.401 79.6568 12.6776 85.8322 15.2309C92.0075 17.7842 97.5297 21.4953 102.399 26.3643L107.03 30.9958L111.662 26.3643C116.412 21.6141 121.904 17.9326 128.139 15.32C134.374 12.7073 140.876 11.401 147.645 11.401C154.414 11.401 160.886 12.6776 167.062 15.2309C173.237 17.7842 178.64 21.4953 183.272 26.3643ZM172.228 86.9302C175.671 83.4862 178.284 79.5673 180.065 75.1733C181.728 70.898 182.559 66.5337 182.559 62.0804C182.559 57.627 181.728 53.2627 180.065 48.9874C178.284 44.7122 175.671 40.8526 172.228 37.4087C169.021 34.0835 165.28 31.5302 161.005 29.7489C156.73 27.9675 152.276 27.0769 147.645 27.0769C143.013 27.0769 138.56 27.9379 134.285 29.6598C130.01 31.3818 126.15 33.9648 122.706 37.4087L112.731 47.7405C111.187 49.1656 109.316 49.8781 107.119 49.8781C104.922 49.8781 102.992 49.1656 101.33 47.7405L91.3543 37.4087C88.0292 34.0835 84.1993 31.5302 79.8646 29.7489C75.53 27.9675 71.047 27.0769 66.4155 27.0769C61.784 27.0769 57.3306 27.9379 53.0554 29.6598C48.7801 31.3818 45.0393 33.9648 41.8329 37.4087C38.3889 40.7339 35.7763 44.5935 33.9949 48.9874C32.3323 53.2627 31.5011 57.627 31.5011 62.0804C31.5011 66.5337 32.3323 70.898 33.9949 75.1733C35.7763 79.5673 38.3889 83.4862 41.8329 86.9302L107.03 152.127L172.228 86.9302Z" fill="#F1F1F1"/>
      </svg>
      <h6 class="wishlist__empty-title">{{ t('profile.wishlist.empty.title') }}</h6>
      <p class="wishlist__empty-text">{{ t('profile.wishlist.empty.text') }}</p>
      <router-link :to="translations.i18nRoute({ name: 'store' })">
        <ui-button class="wishlist__empty-button">{{ t('buttons.returnToShop') }}</ui-button>
      </router-link>
    </div>
  </div>
</template>

<script setup>
import {useI18n} from "vue-i18n";
import UiButton from "@/components/ui/ui-button.vue";
import {computed} from "vue";
import {useWishlistStore} from "@/stores/wishlist.js";
import ProductCard from "@/components/cards/product-card.vue";
import {Wishlist} from "@/core/api/wishlist.js";
import {useAuthStore} from "@/stores/auth.js";
import translations from "@/core/helpers/translations.js";

const {t} = useI18n()
const authStore = useAuthStore()
const wishlistStore = useWishlistStore()

const accessToken = computed(() => {
  return authStore.accessToken ? authStore.accessToken : ''
})
const wishlistUuid = computed(() => {
  return wishlistStore.wishlist ? wishlistStore.wishlist.uuid : ''
})
const productsInWishlist = computed(() => {
  return wishlistStore.wishlist.products ? wishlistStore.wishlist.products.edges : []
})

const removeFromWishlist = async (productUuid) => {
  const res = await Wishlist.overwriteWishlist('removeWishlistProduct', accessToken.value, wishlistUuid.value, productUuid)

  if (!res.errors) {
    wishlistStore.wishlist = res.data.removeWishlistProduct.wishlist
  }
}
</script>

<style lang="scss" scoped>
.wishlist {
  &__title {
    color: #2F2F2F;
    font-size: 18px;
    font-weight: 600;
    line-height: 30.8px;
    text-transform: uppercase;
  }

  &__list {
    border-top: 1px solid $black;
    padding-top: 20px;
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    row-gap: 50px;
  }

  &__item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;

    &-button {
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 5px;

      & p {
        color: #2F2F2F;
        font-size: 14px;
        font-weight: 500;
        line-height: 22.4px;
      }
    }
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 18px;

    &-title {
      color: #2F2F2F;
      text-align: center;
      font-size: 42px;
      font-weight: 600;
      line-height: 57.6px;
    }

    &-text {
      color: #5A5A5A;
      text-align: center;
      font-size: 16px;
      font-weight: 400;
      line-height: 28.16px;
    }

    &-button {
      width: fit-content;
      padding-block: 15px;
      font-size: 14px;
    }
  }
}

@media (max-width: 1680px) {
  .wishlist {
    &__list {
      grid-template-columns: repeat(4, 1fr);
    }
  }
}

@media (max-width: 1300px) {
  .wishlist {
    &__list {
      grid-template-columns: repeat(3, 1fr);
    }
  }
}

@media (max-width: 1000px) {
  .wishlist {
    &__list {
      grid-template-columns: repeat(2, 1fr);
    }
  }
}

@media (max-width: 750px) {
  .wishlist {
    &__list {
      grid-template-columns: repeat(1, 1fr);
    }

    &__empty {
      gap: 10px;

      & svg {
        width: 150px;
      }

      &-title {
        font-size: 26px;
      }

      &-text {
        font-size: 12px;
        line-height: 16px;
      }

      &-button {
        padding-block: 10px;
      }
    }
  }
}
</style>